import React from "react";
import { Check, X, Plus, RotateCcw, HelpCircle } from "lucide-react";
import { getStructuredRequest, resolvePendingRequest, saveStructuredRequest, structuredRequestEnabled } from "@/lib/vg3StructuredRequest";

const ADDABLE = [
  ["sell"], ["prepare", "sell"], ["renovate", "sell"], ["renovate", "rent_long"], ["rent_long"],
  ["rent_short"], ["rent_student"], ["split", "sell"], ["split", "rent_long"],
];
const STEP = {
  sell: "vendere", prepare: "preparare l'immobile", renovate: "ristrutturare", split: "frazionare",
  rent_long: "affittare con contratto lungo", rent_short: "affittare come casa vacanze",
  rent_student: "affittare a studenti", rent_any: "affittare",
};
const label = (steps) => {
  const t = steps.map((s) => STEP[s] || s).join(" e poi ");
  return t.charAt(0).toUpperCase() + t.slice(1);
};
const key = (steps) => steps.join(">");

function OpRow({ op, onExclude, testid }) {
  return (
    <li className="flex items-start justify-between gap-3 rounded-lg border border-vg2-line px-3 py-2" data-testid={testid}>
      <div>
        <p className="text-sm font-medium">{op.label}</p>
        {!op.calculable && (
          <p className="text-xs text-vg2-muted mt-0.5" data-testid={`${testid}-not-calculable`}>
            Registrata: non ancora calcolabile come operazione unica.
          </p>
        )}
      </div>
      <button type="button" onClick={onExclude} data-testid={`${testid}-exclude`}
              className="text-xs text-vg2-muted hover:text-vg2-red inline-flex items-center gap-1 whitespace-nowrap shrink-0 transition-colors">
        <X size={14} /> Non mi interessa
      </button>
    </li>
  );
}

function Ambiguity({ a, onPick }) {
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-3" data-testid={`abbiamo-capito-ambiguity-${a.id}`}>
      <p className="text-sm font-medium flex items-center gap-2"><HelpCircle size={15} /> {a.question}</p>
      <div className="flex flex-wrap gap-2 mt-2">
        {a.options.map((o) => (
          <button key={key(o.steps)} type="button" onClick={() => onPick(a, o)}
                  data-testid={`abbiamo-capito-ambiguity-${a.id}-option-${key(o.steps)}`}
                  className="rounded-full border border-vg2-line bg-white px-3 py-1 text-xs hover:border-vg2-red transition-colors">
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function PendingChoice({ journeyId, pending, onDone, tid = "abbiamo-capito-pending", hasResults = false }) {
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const src = pending.request.source;
  const act = async (action) => {
    setBusy(true); setErr(null);
    try {
      const res = await resolvePendingRequest(journeyId, action);
      if (action === "new_project") { window.location.assign(`/vg2/${res.journey_id}`); return; }
      onDone(action);
    } catch { setErr("Operazione non riuscita: ricarica la pagina."); setBusy(false); }
  };
  const btn = "rounded-full px-4 py-2 text-sm transition-opacity disabled:opacity-60";
  return (
    <div className="rounded-xl border-2 border-vg2-red/40 bg-red-50/40 p-4 mb-5" data-testid={tid}>
      <p className="text-sm font-medium">Dalla Home hai indicato una nuova richiesta:</p>
      <p className="text-sm italic my-2" data-testid={`${tid}-text`}>«{src.raw_text || pending.request.goal_label}»</p>
      <p className="text-xs text-vg2-muted mb-3">
        {hasResults ? "È diversa dalla richiesta del progetto già valutato." : "È diversa dal progetto in corso."} Come vuoi procedere? Nessuna delle due richieste viene cancellata.
      </p>
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={busy} onClick={() => act("new_project")} data-testid={`${tid}-new-project`}
                className={`${btn} bg-vg2-red text-white`}>Inizia un nuovo progetto</button>
        <button type="button" disabled={busy} onClick={() => act("apply")} data-testid={`${tid}-apply`}
                className={`${btn} border border-vg2-line bg-white`}>Aggiorna il progetto esistente</button>
        <button type="button" disabled={busy} onClick={() => act("keep")} data-testid={`${tid}-keep`}
                className={`${btn} border border-vg2-line bg-white`}>Continua con la richiesta precedente</button>
      </div>
      {err && <p className="text-sm text-vg2-error mt-2" data-testid={`${tid}-error`}>{err}</p>}
    </div>
  );
}

function useStructuredRequest(journeyId) {
  const [state, setState] = React.useState({ enabled: false, data: null, error: null, saving: false });
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    (async () => {
      if (!(await structuredRequestEnabled())) return;
      try {
        setState((s) => ({ ...s, enabled: true, data: null }));
        const data = await getStructuredRequest(journeyId);
        setState((s) => ({ ...s, data }));
      } catch {
        setState((s) => ({ ...s, enabled: true, error: "Non riesco a leggere la richiesta." }));
      }
    })();
  }, [journeyId, tick]);
  const reload = () => setTick((t) => t + 1);
  const save = async (req, change) => {
    setState((s) => ({ ...s, saving: true, error: null }));
    try {
      const body = {
        expected_version: req.version || null,
        operations: change.operations.map(({ steps, origin, units, evidence }) => ({ steps, origin, units, evidence })),
        excluded: change.excluded,
        resolved_ambiguities: change.resolved || [],
        reason: change.reason,
      };
      const data = await saveStructuredRequest(journeyId, body);
      const fresh = await getStructuredRequest(journeyId);
      setState((s) => ({ ...s, saving: false, data: { ...fresh, request: data.request } }));
      return true;
    } catch (e) {
      const msg = e?.response?.status === 409 ? "La richiesta è stata aggiornata altrove: ricarica la pagina." : "Salvataggio non riuscito.";
      setState((s) => ({ ...s, saving: false, error: msg }));
      return false;
    }
  };
  return { ...state, save, reload };
}

const FLOOR = { basement: "seminterrato", ground: "piano terra", first: "primo piano", intermediate: "piano intermedio", top: "ultimo piano" };
const PERT = { balcony: "balcone", terrace: "terrazzo", garden: "giardino", cellar: "cantina", attic: "soffitta", box: "box auto", parking: "posto auto" };
const OCC = { free: "libero", rented: "locato a terzi", owner_occupied: "abitato dal proprietario" };
const COND = { to_renovate: "da ristrutturare", good: "in buono stato", excellent: "in ottimo stato" };

export function propertySummary(h = {}) {
  const type = h.property_type === "detached_house" ? "Casa indipendente" : h.property_type ? "Appartamento" : null;
  const place = [h.municipality, [h.address, h.house_number].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const parts = [[type, place].filter(Boolean).join(" · ")];
  if (h.surface_sqm) parts.push(`${h.surface_sqm} m²`);
  if (h.rooms) parts.push(`${h.rooms} locali`);
  if (h.bedrooms) parts.push(`${h.bedrooms} ${h.bedrooms === 1 ? "camera" : "camere"}`);
  if (h.bathrooms) parts.push(`${h.bathrooms} ${h.bathrooms === 1 ? "bagno" : "bagni"}`);
  if (h.floor) parts.push(FLOOR[h.floor]);
  if (h.elevator) parts.push(h.elevator === "yes" ? "con ascensore" : "senza ascensore");
  (h.pertinenze || []).forEach((k) => parts.push(PERT[k]));
  if (h.occupancy) parts.push(OCC[h.occupancy]);
  if (h.declared_condition || h.condition) parts.push(h.declared_condition || COND[h.condition]);
  return parts.filter(Boolean).join(" · ");
}

export function AbbiamoCapitoCard({ journeyId, onRequest }) {
  const { enabled, data, error, saving, save, reload } = useStructuredRequest(journeyId);
  const [adding, setAdding] = React.useState(false);
  const [confirmed, setConfirmed] = React.useState(false);
  React.useEffect(() => { if (data && onRequest) onRequest(data.request); }, [data, onRequest]);
  if (!enabled) return null;
  if (!data) return error ? <p className="text-sm text-vg2-error mb-6" data-testid="abbiamo-capito-error">{error}</p> : null;
  const req = data.request;
  const mine = req.operations.filter((o) => o.origin !== "suggested");
  const suggested = req.operations.filter((o) => o.origin === "suggested").slice(0, 2);
  const excluded = req.excluded.map((e) => e.steps);
  const openAmb = req.ambiguities.filter((a) => a.status === "open");
  const askOps = openAmb.some((a) => !a.about.length);
  const summary = propertySummary(req.property_hints || {});
  const apply = (ops, exc, reason, resolved) => { setConfirmed(false); return save(req, { operations: ops, excluded: exc, reason, resolved }); };
  const exclude = (op) => apply(mine.filter((o) => o !== op), [...excluded, op.steps], `esclusa: ${op.label}`);
  const restore = (steps) => apply([...mine, { steps, origin: "requested" }], excluded.filter((e) => key(e) !== key(steps)), `ripristinata: ${label(steps)}`);
  const add = (steps) => { setAdding(false); apply([...mine, { steps, origin: "requested" }], excluded.filter((e) => key(e) !== key(steps)), `aggiunta: ${label(steps)}`); };
  const pick = (a, o) => {
    const ops = a.about.length ? mine.map((m) => (key(m.steps) === key(a.about) ? { ...m, steps: o.steps, origin: "requested" } : m)) : [...mine, { steps: o.steps, origin: "requested" }];
    apply(ops, excluded, `confermata: ${o.label}`, [a.id]);
  };
  const unchanged = data.persisted && mine.every((m) => m.origin === "requested");
  const confirm = () => {
    if (unchanged) { setConfirmed(true); return; }
    apply(mine.map((m) => ({ ...m, origin: "requested" })), excluded, "confermata dall'utente").then((ok) => ok && setConfirmed(true));
  };
  return (
    <section className="rounded-2xl border border-vg2-line bg-white p-5 md:p-6 mb-8 shadow-sm" data-testid="abbiamo-capito-card">
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <h2 className="font-display font-semibold text-lg">Abbiamo capito</h2>
        <span className="text-xs text-vg2-muted" data-testid="abbiamo-capito-version">
          {data.persisted ? `Versione ${req.version}` : "Proposta da confermare"}
        </span>
      </div>
      {data.pending && <PendingChoice journeyId={journeyId} pending={data.pending} onDone={reload} />}
      {req.source.raw_text && (
        <p className="text-sm text-vg2-muted italic mb-4" data-testid="abbiamo-capito-original-text">«{req.source.raw_text}»</p>
      )}
      {req.semantic?.status && req.semantic.status !== "ok" && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4"
           data-testid="abbiamo-capito-degraded">
          Lettura semplificata della richiesta: il servizio di interpretazione non è disponibile in questo momento.
          Controlla le operazioni qui sotto e correggile se serve; i risultati saranno esplorativi.
        </p>
      )}
      {summary && (
        <p className="text-sm mb-2" data-testid="abbiamo-capito-property">Immobile: <strong className="font-medium">{summary}</strong></p>
      )}
      <p className="text-sm mb-3" data-testid="abbiamo-capito-goal">
        Obiettivo: {req.objective_code ? <strong>{req.goal_label}</strong>
          : <strong className="text-amber-700" data-testid="abbiamo-capito-goal-unclear">da chiarire: indica qui sotto cosa vuoi valutare</strong>}
      </p>
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-2">Operazioni che vuoi valutare</p>
      {mine.length ? (
        <ul className="space-y-2 mb-4" data-testid="abbiamo-capito-requested">
          {mine.map((o, i) => <OpRow key={key(o.steps) + i} op={o} testid={`abbiamo-capito-requested-${i}`} onExclude={() => exclude(o)} />)}
        </ul>
      ) : <p className="text-sm text-vg2-muted mb-4" data-testid="abbiamo-capito-requested-empty">
          {req.objective_code ? "Nessuna operazione indicata: confronteremo le possibilità principali." : "Nessuna operazione indicata."}
        </p>}
      {openAmb.length > 0 && (
        <div className="space-y-2 mb-4" data-testid="abbiamo-capito-ambiguities">
          {openAmb.map((a) => <Ambiguity key={a.id} a={a} onPick={pick} />)}
        </div>
      )}
      {excluded.length > 0 && (
        <div className="mb-4" data-testid="abbiamo-capito-excluded">
          <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-2">Escluse da te</p>
          <div className="flex flex-wrap gap-2">
            {req.excluded.map((e, i) => (
              <button key={key(e.steps)} type="button" onClick={() => restore(e.steps)} data-testid={`abbiamo-capito-excluded-${i}`}
                      className="rounded-full border border-vg2-line px-3 py-1 text-xs line-through text-vg2-muted hover:no-underline inline-flex items-center gap-1">
                {e.label} <RotateCcw size={12} />
              </button>
            ))}
          </div>
        </div>
      )}
      {suggested.length > 0 && !askOps && (
        <div className="mb-4" data-testid="abbiamo-capito-suggested">
          <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-2">Puoi aggiungere anche</p>
          <div className="flex flex-wrap gap-2">
            {suggested.map((o, i) => (
              <button key={key(o.steps)} type="button" onClick={() => add(o.steps)} data-testid={`abbiamo-capito-suggested-${i}`}
                      className="rounded-full border border-dashed border-vg2-line px-3 py-1 text-xs text-vg2-muted hover:border-vg2-red inline-flex items-center gap-1 transition-colors">
                <Plus size={12} /> {o.label}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3 pt-2">
        <button type="button" disabled={saving || (askOps && !mine.length)} onClick={confirm} data-testid="abbiamo-capito-confirm"
                className="rounded-full bg-vg2-red text-white px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60 transition-opacity">
          <Check size={15} /> {data.persisted ? "Confermo" : "È corretto"}
        </button>
        <button type="button" onClick={() => setAdding((v) => !v)} data-testid="abbiamo-capito-add-toggle"
                className="text-sm text-vg2-muted hover:text-vg2-red inline-flex items-center gap-1 transition-colors">
          <Plus size={15} /> Aggiungi un'operazione
        </button>
        {saving && <span className="text-xs text-vg2-muted" data-testid="abbiamo-capito-saving">Salvataggio…</span>}
        {confirmed && !saving && (
          <span className="text-xs text-emerald-700 inline-flex items-center gap-1" data-testid="abbiamo-capito-confirmed">
            <Check size={13} /> Richiesta confermata: completa i dati dell’immobile qui sotto.
          </span>
        )}
      </div>
      {adding && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid="abbiamo-capito-add-list">
          {ADDABLE.filter((s) => !mine.some((m) => key(m.steps) === key(s))).map((s) => (
            <button key={key(s)} type="button" onClick={() => add(s)} data-testid={`abbiamo-capito-add-${key(s)}`}
                    className="rounded-full border border-vg2-line px-3 py-1 text-xs hover:border-vg2-red transition-colors">{label(s)}</button>
          ))}
        </div>
      )}
      {error && <p className="text-sm text-vg2-error mt-3" data-testid="abbiamo-capito-error">{error}</p>}
      <p className="text-xs text-vg2-muted mt-4" data-testid="abbiamo-capito-note">
        La richiesta viene registrata così come la confermi: le operazioni elencate sono quelle che verranno valutate. In più ValutaGenio mostrerà al massimo 2 alternative, separate e solo come confronto.
      </p>
    </section>
  );
}
